import { useEffect, useState } from "react";
import inksolverLogo from "../assets/inksolver-logo.png";

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="3" />
      <path d="m5 8 7 5 7-5" />
    </svg>
  );
}

export default function EmailVerificationScreen({
  email,
  pendingEmail,
  sending,
  changing,
  checking,
  error,
  notice,
  onResend,
  onChangeEmail,
  onSignOut
}) {
  const [editingEmail, setEditingEmail] = useState(false);
  const [nextEmail, setNextEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const targetEmail = pendingEmail || email || "your email address";

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((current) => Math.max(0, current - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  async function handleResend() {
    const sent = await onResend();
    if (sent) setResendCooldown(45);
  }

  async function handleEmailChange(event) {
    event.preventDefault();
    const changed = await onChangeEmail(nextEmail);
    if (changed) {
      setNextEmail("");
      setEditingEmail(false);
      setResendCooldown(45);
    }
  }

  return (
    <main className="verification-page">
      <header className="login-topbar verification-topbar">
        <img src={inksolverLogo} alt="InkSolver" className="login-brand-logo" />
        <span className="login-brand-pill">Ink-driven math practice</span>
      </header>

      <section className="verification-stage">
        <div className="verification-card">
          <div className="verification-mail-icon"><MailIcon /></div>
          <p className="verification-eyebrow">ONE QUICK CHECK</p>
          <h1>Check your inbox</h1>
          <p className="verification-copy">
            We sent a verification link to <strong>{targetEmail}</strong>. Open it and we will bring you into InkSolver automatically.
          </p>

          <div className={`verification-listener ${checking ? "checking" : ""}`.trim()} role="status" aria-live="polite">
            <span className="verification-pulse" aria-hidden="true" />
            <span>{checking ? "Confirming your email..." : "Waiting securely for verification"}</span>
          </div>

          {error && <div className="login-message error" role="alert">{error}</div>}
          {notice && <div className="login-message success" role="status">{notice}</div>}

          {editingEmail ? (
            <form className="verification-change-form" onSubmit={handleEmailChange}>
              <label htmlFor="verification-new-email">Use a different email</label>
              <input
                id="verification-new-email"
                type="email"
                value={nextEmail}
                onChange={(event) => setNextEmail(event.target.value)}
                placeholder="correct-email@example.com"
                autoComplete="email"
                required
                autoFocus
              />
              <div className="verification-form-actions">
                <button type="button" className="verification-text-button" onClick={() => setEditingEmail(false)} disabled={changing}>
                  Cancel
                </button>
                <button type="submit" className="verification-primary-button" disabled={changing}>
                  {changing ? "Sending..." : "Send verification"}
                </button>
              </div>
            </form>
          ) : (
            <div className="verification-actions">
              <button
                type="button"
                className="verification-primary-button"
                onClick={handleResend}
                disabled={sending || resendCooldown > 0}
              >
                {sending ? "Sending..." : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend email"}
              </button>
              <button type="button" className="verification-secondary-button" onClick={() => setEditingEmail(true)}>
                Use a different email
              </button>
            </div>
          )}

          <button type="button" className="verification-signout" onClick={onSignOut}>Sign out</button>
        </div>
      </section>
    </main>
  );
}
